import React, { FC, Fragment, useEffect, useState, Suspense, useRef, useDeferredValue, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Heading,
  IconSize,
  Text,
  TextFieldSize,
  Icon,
  MenuOptions,
  Menu,
  useSideSheetState,
  theme,
  Spinner,
  SpinnerSize,
  Button,
  ToastType,
  useToastsDispatch,
  ToastAction,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, isEqual, map, get, find, compact, forEach, includes, reduce } from 'lodash';
import {
  EllipsisPlacement,
  ErrorBoundary,
  TextWithEllipsis,
  useSessionStorage,
  CacheKeys,
  SuspenseLoader,
  useAppUser,
  ErrorRetry,
  TOAST_ID_NEW_ROD_REQUEST,
} from '@bechtel/client-common';
import { RodRequestItem } from './item';
import { RodRequestItemTab, RodRoomRequestListProps } from './types';
import { rodRequestItemTabList } from './tab-data';
import { pickupTimeList, RodRequestDrawerValuesProps, searchByList, sortByList } from './drawer';
import { drawerValues as defaultValues } from './drawer';
import { CreateAdhocRodRequest } from '../create/adhoc';
import { PickupTimeTerm } from '../types';
import {
  ListStyled,
  AddRodRequestButtonStyled,
  NavLinkStyled,
  RodRequestListContainerStyled,
  RodRequestListHeaderStyled,
  PopperContainerStyled,
  SearchFieldStyled,
  MenuItemContainerStyled,
  MenuItemStyled,
  TabButtonStyled,
  SearchFieldComponentStyled,
  SearchMenuItemContainerStyled,
  FilterContainerStyled,
  LabelStyled,
  SearchMenuItemStyled,
  ArcStrykerLogoStyled,
} from './styled';
import {
  FilterOperator,
  PagingInput,
  PersonaType,
  RodRequest,
  RodRequestComplexFilterInput,
  RodRequestFilterByTerm,
  RodRequestFilterInput,
  RodRequestList,
  RodRequestSortByTerm,
  RodRequestStatusCode,
  RodRequestStatusStats,
  RodRoom,
  SortOrder,
} from '@bechtel/types';
// @ts-ignore: svg declarations not recognising
import { ReactComponent as ArcStrykerSmallLogo } from '@bechtel/client-assets/dist/images/arc-stryker-small-logo.svg';
import { GET_ROD_REQUESTS } from '@bechtel/client-gql';
import { useLazyQuery } from '@apollo/client';
import { useRodRoomListQuery, useRodRoomPermissions } from '@bechtel/client-weld-common';
import { useRodRequestSubscriptionContext } from '~/components/atoms/rod-request-subscription-context';
import { useRodRequestsDetailsByIdsQuery } from '~/hooks';
import { RodRoomDataContext } from '~/utilities/context';

export const RodRoomRequestList: FC<RodRoomRequestListProps> = ({ rodRequestStats }) => {
  const { selectedRodRoom, setSelectedRodRoom } = useContext(RodRoomDataContext);
  const { user } = useAppUser();
  const location = useLocation();
  const { canCreateAdhocRodRequest } = useRodRoomPermissions();
  const dispatchToast = useToastsDispatch();
  const { rodRequestId, setRodRequestId } = useRodRequestSubscriptionContext();
  const { i18n } = useLingui();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTabItem, setSelectedTabItem] = useState<RodRequestItemTab>(rodRequestItemTabList[0]);
  const navigate = useNavigate();
  const debouncedSearchQuery = useDeferredValue(searchQuery?.trim());
  const pageLimit = 10;
  const {
    visible: visibleCreateAdhocRodRequest,
    open: openCreateAdhocRodRequest,
    close: closeCreateAdhocRodRequest,
  } = useSideSheetState(false);
  // NOTE: we need a useRef here otherwise route transitions will "reset" the state for drawerValues and fall back to the "defaultValues"
  const persistedDrawerValues = useRef(defaultValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<RodRequestDrawerValuesProps>(
    CacheKeys.RodRequestListDrawerValues,
    persistedDrawerValues.current || defaultValues
  );
  const [
    loadRodRequests,
    {
      loading: rodRequestLoading,
      data: rodRequestData,
      error: rodRequestError,
      refetch: rodRequestRefetch,
      called: rodRequestCalled,
      fetchMore: rodRequestFetchMore,
    },
  ] = useLazyQuery<
    {
      rodRequests: RodRequestList;
    },
    {
      input: RodRequestFilterInput;
      paging?: PagingInput;
    }
  >(GET_ROD_REQUESTS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const rodRequestList: RodRequest[] = get(rodRequestData, 'rodRequests.items', []);
  const rodRequestTotalRecords = get(rodRequestData, 'rodRequests.total', 0);
  const rodRequestTotalPages = rodRequestTotalRecords ? Math.ceil(rodRequestTotalRecords / pageLimit) : 0;
  const rodRequestCursor = get(rodRequestData, 'rodRequests.cursor', 1);
  const filtetredRodRequestList: RodRequest[] = filter(rodRequestList, (item) =>
    includes(selectedTabItem.value, item.currentStatusI18n)
  );

  useEffect(() => {
    if (selectedRodRoom) return;
    navigate('/rod-room');
  }, [navigate, selectedRodRoom]);

  const { loading, error, data, refetch } = useRodRoomListQuery(true);
  const rodRoomList = get(data, 'rodRooms', []);
  const createdRodRequestId = useRef('');
  const [loadRodRequestDetailsByIds, { data: rodRequestDetailData }] = useRodRequestsDetailsByIdsQuery(true);

  const getRodRequestFilterInput = () => {
    const rodRequestFilterInput: RodRequestFilterInput = {
      sortByFilter: {
        term: drawerValues?.sortBy ? drawerValues?.sortBy : RodRequestSortByTerm.REQUEST_TIME,
        sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortOrder.DESC,
      },
    };
    const rodRequestComplexFilter: RodRequestComplexFilterInput = {
      and: [
        {
          term: RodRequestFilterByTerm.ROD_ROOM_ID,
          value: selectedRodRoom?.id,
          operator: FilterOperator.EQUALS,
        },
        {
          term: RodRequestFilterByTerm.ROD_REQUEST_STATUS_ID,
          values: selectedTabItem.value,
          operator: FilterOperator.IN,
        },
      ],
    };
    if (drawerValues?.pickupTimeList?.length > 0 || drawerValues?.pickupTimeList?.length < 3)
      forEach(drawerValues.pickupTimeList, (item: PickupTimeTerm) => {
        rodRequestComplexFilter.or?.push({
          term: RodRequestFilterByTerm.ROD_REQUEST_DATE,
          value: new Date().toISOString(),
          operator:
            item === PickupTimeTerm.TODAY
              ? FilterOperator.EQUALS
              : item === PickupTimeTerm.TOMORROW
              ? FilterOperator.GREATER_THAN
              : FilterOperator.LESS_THAN,
        });
      });
    if (debouncedSearchQuery)
      forEach(drawerValues.searchByList, (item: RodRequestFilterByTerm) => {
        rodRequestComplexFilter.or?.push({
          term: item,
          value: debouncedSearchQuery,
          operator:
            item === RodRequestFilterByTerm.WELDER || item === RodRequestFilterByTerm.REQUESTER
              ? FilterOperator.FUZZY
              : FilterOperator.CONTAINS,
        });
      });
    rodRequestFilterInput.complexFilter = rodRequestComplexFilter;
    return rodRequestFilterInput;
  };

  useEffect(() => {
    if (!selectedRodRoom || (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3)) return;
    loadRodRequests({
      variables: {
        input: getRodRequestFilterInput(),
        paging: {
          cursor: '1',
          limit: pageLimit,
        },
      },
    });
  }, [selectedRodRoom, selectedTabItem, debouncedSearchQuery, drawerValues, loadRodRequests]);

  const NewRodRequestToast = () => {
    return (
      <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '450' }}>
          <Text family="graphik" weight="600" size="500">
            <Trans>New rod request is created for this rod room.</Trans>
          </Text>
        </Box>
        <Text family="graphik" weight="600" size="500">
          <Trans>Please REFETCH to view the latest requests.</Trans>
        </Text>
      </Box>
    );
  };

  const toastNewRodRequest: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      toastType: ToastType.INFO,
      id: TOAST_ID_NEW_ROD_REQUEST,
      content: NewRodRequestToast,
      alertAction: {
        label: 'REFETCH',
        onClickHandler: () => {
          loadRodRequests({
            variables: {
              input: getRodRequestFilterInput(),
              paging: {
                cursor: '1',
                limit: pageLimit,
              },
            },
          });
        },
      },
    },
  };

  useEffect(() => {
    if (!rodRequestId || rodRequestId == '' || rodRequestId === createdRodRequestId.current) return;
    loadRodRequestDetailsByIds({
      variables: {
        ids: [rodRequestId],
      },
    });
  }, [rodRequestId]);

  useEffect(() => {
    if (!rodRequestId || rodRequestId == '' || rodRequestId === createdRodRequestId.current || !rodRequestDetailData)
      return;
    const rodRequest: RodRequest | undefined = get(rodRequestDetailData, 'rodRequestsByIds[0]');

    if (
      !rodRequest ||
      rodRequest.id != rodRequestId ||
      rodRequest.rodRoom.id !== selectedRodRoom?.id ||
      (rodRequest.currentStatusI18n === RodRequestStatusCode.ISSUED &&
        includes(selectedTabItem.value, RodRequestStatusCode.ISSUED)) ||
      (rodRequest.currentStatusI18n === RodRequestStatusCode.REQUESTED &&
        includes(selectedTabItem.value, RodRequestStatusCode.REQUESTED)) ||
      find(rodRequestList, (rodRequestItem) => rodRequestItem.id === rodRequestId)
    )
      return;
    if (rodRequest.createdBy.id == user?.id) {
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: TOAST_ID_NEW_ROD_REQUEST } }); //Remove before adding new toast
      setRodRequestId('');
      dispatchToast(toastNewRodRequest);
    }
  }, [rodRequestDetailData, rodRequestId]);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      rodRequestCursor <= rodRequestTotalPages &&
      !rodRequestLoading
    ) {
      rodRequestFetchMore({
        variables: {
          paging: {
            cursor: rodRequestCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (!rodRequestList || rodRequestList.length === 0) return;
    if (location.pathname === '/rod-room/requests') {
      const [rodRequest] = rodRequestList;
      const encodedId = encodeURIComponent(rodRequest.id);
      navigate(`${encodedId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RTN causing it to render again since data in rodRequestList getting changed so added dependency on rodRequestList.length instead of rodRequestList
  }, [navigate, location.pathname, rodRequestList.length]);

  const searchingByLabel = map(drawerValues.searchByList, (search: any) => {
    return get(
      find(searchByList, {
        value: search,
      }),
      'label'
    );
  });

  const filteringByLabel = map(drawerValues.pickupTimeList, (filter: any) => {
    return get(
      find(pickupTimeList, {
        value: filter,
      }),
      'label'
    );
  });

  const sortingByLabel = get(
    find(sortByList, {
      value: drawerValues.sortBy,
    }),
    'label'
  );

  const getRodRequestStat = (requestStatusCodes: RodRequestStatusCode[]) => {
    const statusStat: RodRequestStatusStats[] = filter(
      rodRequestStats?.rodRequestSummary,
      (item: RodRequestStatusStats) => includes(requestStatusCodes, item?.status)
    );
    return reduce(statusStat, (acc, item) => acc + item.stats, 0);
  };

  return (
    <Fragment>
      <RodRequestListContainerStyled data-testid="rod-request-list-container">
        <RodRequestListHeaderStyled data-testid="rod-request-list-header">
          <Box mb={{ sm: '450' }} alignItems={{ sm: 'center' }} css={{ background: `${theme.colors.grey[100]}` }}>
            {user?.crewMember?.persona === PersonaType.ROD_ROOM_ATTENDANT && (
              <ArcStrykerLogoStyled>
                <ArcStrykerSmallLogo />
              </ArcStrykerLogoStyled>
            )}
            <Box
              justifyContent={{ sm: 'space-between' }}
              css={{ width: '100%' }}
              alignItems={{ sm: 'center' }}
              px={{ sm: '500' }}
              py={{ sm: '450' }}
            >
              <Box flexDirection={{ sm: 'column' }}>
                <Heading size="600" weight="600" color="grey.1000" data-testid="rod-request-title">
                  <Trans>Rod Request(s)</Trans>
                </Heading>
                <Box css={{ position: 'relative' }} mt={{ sm: '400' }} data-testid="rod-room-menu-container">
                  <Menu>
                    {({ isOpen, toggle, close: closeMenu }) => (
                      <div
                        onBlur={() => {
                          closeMenu();
                        }}
                      >
                        <Box
                          alignItems={{ sm: 'center' }}
                          css={{ cursor: 'pointer' }}
                          onClick={() => {
                            toggle();
                          }}
                        >
                          <Box mr={{ sm: '400' }}>
                            <Text size="400" color="bechtel.blue.700" family="graphik">
                              {selectedRodRoom?.name}
                            </Text>
                          </Box>
                          <Icon icon="caret-down" size={IconSize.XSMALL} fillColor="blue.800" />
                        </Box>
                        <PopperContainerStyled data-testid="menu-popper-container">
                          {isOpen && (
                            <MenuOptions
                              css={`
                                order: 2;
                              `}
                            >
                              <MenuItemContainerStyled>
                                <Box
                                  flexDirection={{ sm: 'column' }}
                                  flexGrow={{ sm: '1' }}
                                  pt={{ sm: '400' }}
                                  css={{ overflowY: 'auto' }}
                                >
                                  {!loading &&
                                    !error &&
                                    map(rodRoomList, (rodRoom: RodRoom) => {
                                      return (
                                        <MenuItemStyled
                                          key={rodRoom.id}
                                          onMouseDown={(event) => {
                                            setSelectedRodRoom(rodRoom);
                                            window.sessionStorage.setItem(
                                              CacheKeys.SelectedRodRoom,
                                              JSON.stringify(rodRoom)
                                            );
                                            closeMenu();
                                            event.preventDefault();
                                          }}
                                        >
                                          <Box
                                            justifyContent={{ sm: 'space-between' }}
                                            alignItems={{ sm: 'center' }}
                                            pl={{ sm: '400' }}
                                          >
                                            <Text size="500">{rodRoom.name}</Text>
                                            {isEqual(rodRoom.id, selectedRodRoom?.id) && (
                                              <Icon icon="check" size={IconSize.MEDIUM} fillColor="blue.800" />
                                            )}
                                          </Box>
                                        </MenuItemStyled>
                                      );
                                    })}
                                </Box>
                                {!loading && error && (
                                  <Box>
                                    <ErrorRetry refetch={refetch}>
                                      <Trans>
                                        There was a problem loading Rod Room List. Please click the button below to try
                                        again.
                                      </Trans>
                                    </ErrorRetry>
                                  </Box>
                                )}
                              </MenuItemContainerStyled>
                            </MenuOptions>
                          )}
                        </PopperContainerStyled>
                      </div>
                    )}
                  </Menu>
                </Box>
              </Box>
              {canCreateAdhocRodRequest && (
                <AddRodRequestButtonStyled data-testid="add-new-container" onClick={() => openCreateAdhocRodRequest()}>
                  <Box
                    justifyContent={{ sm: 'center' }}
                    alignItems={{ sm: 'center' }}
                    data-testid="plus-icon-container"
                  >
                    <Icon icon="plus" iconStyle="solid" size={IconSize.MEDIUM} fillColor="blue.800" />
                  </Box>
                </AddRodRequestButtonStyled>
              )}
            </Box>
          </Box>
          <SearchFieldStyled data-testid="search-field-container">
            <SearchFieldComponentStyled
              size={TextFieldSize.MEDIUM}
              iconSize={IconSize.SMALL}
              placeholder={t`Search Welders`}
              type="search"
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              value={searchQuery}
            />
          </SearchFieldStyled>
          <FilterContainerStyled data-testid="filter-container">
            <Box
              py={{ sm: '300' }}
              alignItems={{ sm: 'center' }}
              css={{ cursor: 'pointer' }}
              pl={{ sm: '200' }}
              onClick={(event) => {
                const updatedValue = {
                  ...drawerValues,
                  sortDir: drawerValues?.sortDir === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC,
                };
                setDrawerValues(() => {
                  persistedDrawerValues.current = updatedValue;
                  return updatedValue;
                });
                event.preventDefault();
              }}
            >
              <Box mr={{ sm: '300' }}>
                <Icon
                  style={{ transform: drawerValues?.sortDir === SortOrder.DESC ? 'rotate(180deg)' : '' }}
                  fillColor="bechtel.blue.700"
                  icon="sort-up"
                  size={IconSize.SMALL}
                />
              </Box>
              <TextWithEllipsis
                color="grey.1000"
                size="200"
                weight="400"
                title={sortingByLabel}
                data-testid="sorting-text"
              >
                {i18n._(sortingByLabel)} {drawerValues?.sortDir === SortOrder.DESC ? t`(Desc)` : t`(Asc)`}
              </TextWithEllipsis>
            </Box>
            <Box data-testid="searchby-container" css={{ position: 'relative' }} alignItems={{ sm: 'center' }}>
              <Menu data-testid="searchby-menu">
                {({ isOpen, toggle, close: closeMenu }) => (
                  <div
                    onBlur={() => {
                      closeMenu();
                    }}
                  >
                    <Box
                      alignItems={{ sm: 'center' }}
                      css={{ cursor: 'pointer' }}
                      onClick={() => {
                        toggle();
                      }}
                    >
                      <Box py={{ sm: '300' }}>
                        <Box mr={{ sm: '300' }}>
                          <Icon fillColor="bechtel.blue.700" icon="file-search" size={IconSize.SMALL} />
                        </Box>
                        <LabelStyled>
                          <TextWithEllipsis
                            size="200"
                            title={compact(searchingByLabel)?.join(' , ')}
                            data-testid="searching-by-text"
                            color="grey.1000"
                          >
                            {searchingByLabel.length > 0
                              ? searchingByLabel.length === searchByList.length
                                ? t`Search All`
                                : i18n._(compact(searchingByLabel)?.join(' , '))
                              : ''}
                          </TextWithEllipsis>
                        </LabelStyled>
                      </Box>
                    </Box>
                    <PopperContainerStyled data-testid="search-popper-container">
                      {isOpen && (
                        <MenuOptions>
                          <SearchMenuItemContainerStyled>
                            <Box
                              flexDirection={{ sm: 'column' }}
                              flexGrow={{ sm: '1' }}
                              pt={{ sm: '400' }}
                              css={{ overflowY: 'auto' }}
                            >
                              {map(searchByList, (item) => {
                                return (
                                  <SearchMenuItemStyled
                                    key={item.value}
                                    onMouseDown={(event) => {
                                      let updatedValue;
                                      if (!find(drawerValues.searchByList, (value) => value == item.value)) {
                                        updatedValue = {
                                          ...drawerValues,
                                          searchByList: [...drawerValues.searchByList, item.value],
                                        };
                                      } else {
                                        updatedValue = {
                                          ...drawerValues,
                                          searchByList: [
                                            ...filter(drawerValues.searchByList, (value) => value !== item.value),
                                          ],
                                        };
                                      }
                                      setDrawerValues(() => {
                                        persistedDrawerValues.current = updatedValue;
                                        return updatedValue;
                                      });
                                      closeMenu();
                                      event.preventDefault();
                                    }}
                                  >
                                    <Box
                                      justifyContent={{ sm: 'space-between' }}
                                      alignItems={{ sm: 'center' }}
                                      pl={{ sm: '100' }}
                                    >
                                      <Text size="200">{i18n._(item.label)}</Text>
                                      <Box pl={{ sm: '300' }}>
                                        {find(drawerValues.searchByList, (value) => value == item.value) && (
                                          <Icon icon="check" size={IconSize.MEDIUM} fillColor="blue.800" />
                                        )}
                                      </Box>
                                    </Box>
                                  </SearchMenuItemStyled>
                                );
                              })}
                            </Box>
                          </SearchMenuItemContainerStyled>
                        </MenuOptions>
                      )}
                    </PopperContainerStyled>
                  </div>
                )}
              </Menu>
            </Box>
            <Box data-testid="pickup-time-container" css={{ position: 'relative' }} alignItems={{ sm: 'center' }}>
              <Menu>
                {({ isOpen, toggle, close: closeMenu }) => (
                  <div
                    onBlur={() => {
                      closeMenu();
                    }}
                  >
                    <Box
                      alignItems={{ sm: 'center' }}
                      css={{ cursor: 'pointer' }}
                      onClick={() => {
                        toggle();
                      }}
                    >
                      <Box mr={{ sm: '400' }} py={{ sm: '300' }}>
                        <Box mr={{ sm: '300' }}>
                          <Icon fillColor="bechtel.blue.700" icon="filter" size={IconSize.SMALL} />
                        </Box>
                        <LabelStyled>
                          <TextWithEllipsis
                            size="200"
                            title={compact(filteringByLabel)?.join(' , ')}
                            data-testid="pickup-time-text"
                            ellipsisPlacement={EllipsisPlacement.END}
                            color="grey.1000"
                          >
                            {filteringByLabel.length > 1 && filteringByLabel.length === pickupTimeList.length
                              ? t`Pickup Time (All)`
                              : filteringByLabel?.length != 0
                              ? t`Pickup Time (${i18n._(compact(filteringByLabel)?.join(' , '))})`
                              : t`Pickup Time (All)`}
                          </TextWithEllipsis>
                        </LabelStyled>
                      </Box>
                    </Box>
                    <PopperContainerStyled data-testid="pickupTime-popper-container">
                      {isOpen && (
                        <MenuOptions>
                          <SearchMenuItemContainerStyled>
                            <Box
                              flexDirection={{ sm: 'column' }}
                              flexGrow={{ sm: '1' }}
                              pt={{ sm: '400' }}
                              css={{ overflowY: 'auto' }}
                            >
                              {map(pickupTimeList, (item) => {
                                return (
                                  <SearchMenuItemStyled
                                    key={item.value}
                                    onMouseDown={(event) => {
                                      let updatedValue;
                                      if (!find(drawerValues.pickupTimeList, (value) => value == item.value)) {
                                        updatedValue = {
                                          ...drawerValues,
                                          pickupTimeList: [...drawerValues.pickupTimeList, item.value],
                                        };
                                      } else {
                                        updatedValue = {
                                          ...drawerValues,
                                          pickupTimeList: [
                                            ...filter(drawerValues.pickupTimeList, (value) => value !== item.value),
                                          ],
                                        };
                                      }
                                      setDrawerValues(() => {
                                        persistedDrawerValues.current = updatedValue;
                                        return updatedValue;
                                      });
                                      closeMenu();
                                      event.preventDefault();
                                    }}
                                  >
                                    <Box
                                      justifyContent={{ sm: 'space-between' }}
                                      alignItems={{ sm: 'center' }}
                                      pl={{ sm: '100' }}
                                    >
                                      <Text size="200">{i18n._(item.label)}</Text>
                                      <Box pl={{ sm: '300' }}>
                                        {find(drawerValues.pickupTimeList, (value) => value == item.value) && (
                                          <Icon icon="check" size={IconSize.MEDIUM} fillColor="blue.800" />
                                        )}
                                      </Box>
                                    </Box>
                                  </SearchMenuItemStyled>
                                );
                              })}
                            </Box>
                          </SearchMenuItemContainerStyled>
                        </MenuOptions>
                      )}
                    </PopperContainerStyled>
                  </div>
                )}
              </Menu>
            </Box>
          </FilterContainerStyled>
        </RodRequestListHeaderStyled>
        <Box
          justifyContent={{ sm: 'space-between' }}
          data-testid="tabs-container"
          mt={{ sm: '200' }}
          css={{ boxShadow: `0 2px 4px 0 rgba(0, 0, 0, 0.2)` }}
        >
          {map(rodRequestItemTabList, (tabItem: RodRequestItemTab) => {
            return (
              <TabButtonStyled
                key={tabItem.label}
                data-testid="tab-button"
                selected={tabItem.label === selectedTabItem.label}
                onClick={(event) => {
                  event.preventDefault();
                  setSelectedTabItem(tabItem);
                }}
              >
                <Text>
                  {i18n._(tabItem.label)}&nbsp;
                  <Text>{`(${getRodRequestStat(tabItem.value)})`}</Text>
                </Text>
              </TabButtonStyled>
            );
          })}
        </Box>

        <ListStyled
          onScroll={(e) => {
            handleScroll(e);
          }}
        >
          {filtetredRodRequestList &&
            filtetredRodRequestList?.length > 0 &&
            map(filtetredRodRequestList, (rodRequest: RodRequest) => {
              const encodedId = encodeURIComponent(rodRequest.id);
              return (
                <NavLinkStyled to={`${encodedId}`} key={encodedId}>
                  <ErrorBoundary>
                    <RodRequestItem {...rodRequest} />
                  </ErrorBoundary>
                </NavLinkStyled>
              );
            })}
          {rodRequestLoading && (
            <Box
              p={{ sm: '500' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              css={filtetredRodRequestList.length === 0 ? 'height: 100%;' : ''}
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
          {!rodRequestLoading && rodRequestError && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '500' }}
            >
              <Box pb={{ sm: '500' }}>
                <Text>
                  <Trans>There was a problem loading Rod Requests. Please click the button below to try again.</Trans>
                </Text>
              </Box>
              <Button onClick={() => rodRequestRefetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          )}
          {rodRequestCalled && !rodRequestLoading && !rodRequestError && filtetredRodRequestList?.length === 0 && (
            <Box
              flexDirection={{
                sm: 'column',
              }}
              alignItems={{
                sm: 'center',
              }}
              pt={{ sm: '900' }}
            >
              <Heading size="400" weight="600" color="grey.600">
                <Trans> No Rod Request found.</Trans>
              </Heading>
              <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                <Text
                  size="400"
                  family="graphik"
                  color="grey.600"
                  style={{
                    textAlign: 'center',
                    lineHeight: '1.57',
                  }}
                >
                  <Trans>Try changing your search criteria or editing your filters.</Trans>
                </Text>
              </Box>
            </Box>
          )}
        </ListStyled>
      </RodRequestListContainerStyled>
      {visibleCreateAdhocRodRequest && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CreateAdhocRodRequest onClose={closeCreateAdhocRodRequest} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
